import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ensureStickyHeaderVisible, isMobileBreakpoint } from '@theme/utilities';

const CART_AUTO_CLOSE_DELAY = 5000;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #positionFrame = null;

  /** @type {number | null} */
  #autoCloseTimeout = null;

  /**
   * Header attributes from before the drawer made it visible.
   * @type {{ element: HTMLElement, stickyState: string | null, scrollDirection: string | null } | null}
   */
  #headerStateBeforeOpen = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateContextualPosition);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#resetContextualPosition);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreHeaderState);
    window.addEventListener('resize', this.#handleResize);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateContextualPosition);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#resetContextualPosition);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreHeaderState);
    this.#historyAbortController?.abort();
    window.removeEventListener('resize', this.#handleResize);
    this.#cancelPositionFrame();
    this.#clearAutoCloseTimeout();
    this.#restoreHeaderState();
  }

  #handleResize = () => {
    if (!this.refs.dialog?.open) return;
    this.#updateContextualPosition();
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #cancelPositionFrame() {
    if (this.#positionFrame === null) return;
    cancelAnimationFrame(this.#positionFrame);
    this.#positionFrame = null;
  }

  #resetContextualPosition = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#cancelPositionFrame();
    dialog.removeAttribute('data-positioned');
    dialog.style.removeProperty('--cart-contextual-top');
    dialog.style.removeProperty('--cart-contextual-left');
    dialog.style.removeProperty('--cart-contextual-right');
  };


  #clearAutoCloseTimeout = () => {
    if (this.#autoCloseTimeout === null) return;
    clearTimeout(this.#autoCloseTimeout);
    this.#autoCloseTimeout = null;
  };

  #scheduleAutoClose = () => {
    this.#clearAutoCloseTimeout();

    this.#autoCloseTimeout = window.setTimeout(async () => {
      this.#autoCloseTimeout = null;

      if (!this.refs.dialog?.open) return;

      await this.closeDialog();
    }, CART_AUTO_CLOSE_DELAY);
  };

  #updateContextualPosition = () => {
    if (!this.isContextualMode || isMobileBreakpoint()) {
      this.#resetContextualPosition();
      return;
    }

    const { dialog } = this.refs;
    const trigger = this.querySelector('[data-cart-trigger]');

    if (!dialog || !(trigger instanceof HTMLElement)) return;

    this.#cancelPositionFrame();
    this.#positionFrame = requestAnimationFrame(() => {
      const triggerRect = trigger.getBoundingClientRect();
      const dialogRect = dialog.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const spacing = 16;
      const top = triggerRect.bottom + 12;
      const preferredLeft = triggerRect.right - dialogRect.width;
      const maxLeft = viewportWidth - dialogRect.width - spacing;
      const left = Math.min(Math.max(preferredLeft, spacing), Math.max(maxLeft, spacing));

      dialog.style.setProperty('--cart-contextual-top', `${top}px`);
      dialog.style.setProperty('--cart-contextual-left', `${left}px`);
      dialog.style.setProperty('--cart-contextual-right', 'auto');
      dialog.setAttribute('data-positioned', 'true');
      this.#positionFrame = null;
    });
  };

  #showStickyHeader() {
    const header = document.querySelector('#header-component');

    if (this.#headerStateBeforeOpen === null && header instanceof HTMLElement) {
      this.#headerStateBeforeOpen = {
        element: header,
        stickyState: header.getAttribute('data-sticky-state'),
        scrollDirection: header.getAttribute('data-scroll-direction'),
      };
    }

    ensureStickyHeaderVisible();
  }

  #restoreHeaderState = () => {
    const previousState = this.#headerStateBeforeOpen;
    this.#headerStateBeforeOpen = null;

    if (!previousState?.element.isConnected) return;

    const restoreAttribute = (name, value) => {
      if (value === null) {
        previousState.element.removeAttribute(name);
      } else {
        previousState.element.setAttribute(name, value);
      }
    };

    restoreAttribute('data-sticky-state', previousState.stickyState);
    restoreAttribute('data-scroll-direction', previousState.scrollDirection);
  };

  openTemporarily() {
    this.#clearAutoCloseTimeout();
    this.#showStickyHeader();
    this.showDialog();
    this.#scheduleAutoClose();
  }

  open() {
    this.#clearAutoCloseTimeout();
    this.#showStickyHeader();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.#clearAutoCloseTimeout();
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  get isContextualMode() {
    return this.dataset.displayMode === 'contextual';
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
